import { useCallback, useMemo } from "react";

import { useGetQueryData } from "../react-query-hooks/general";
import { __CheckAccessToModule } from "../accessControl";
import { useCollabDoc } from "./useCollabDoc";

const arraysEqual = (a, b) =>
  a.length === b.length && a.every((x, i) => x === b[i]);

/** The widget blob as stored in the Y.Doc: everything but layout. Applied to
 *  every blob WRITE and to every blob COMPARE — if gridColumn ever leaks into a
 *  content write, structural ops go back to clobbering sibling edits. */
const stripLayout = (w) => {
  if (!w || w.gridColumn === undefined) return w;
  const { gridColumn, ...rest } = w;
  return rest;
};

/**
 * Live collab session for one report (reportDoc). Reports are the single-doc
 * case (like PublishedPageV3) — one report = one Y.Doc session.
 *
 * Enabling collab makes the report ALWAYS-LIVE: the working `config` comes from
 * the Y.Doc, edits flow to `setField`, and the flush is the save (the manual
 * "Publish" write is retired in collab mode). The Y.Doc holds `title`,
 * `subtitle`, `__configOrder` (widget-id order), and one entry per widget keyed
 * by its stable id (`config.<widgetId>`) — so two people editing different
 * widgets never clobber each other, and locks are keyed by widget id (stable
 * across reorder), not array index.
 *
 * LAYOUT is split from content: `gridColumn` lives under `layout.<widgetId>`,
 * NOT in the widget blob. Structural ops (add/delete/reorder) redistribute
 * sibling gridColumns — with layout in the blob they'd rewrite sibling blobs
 * wholesale and clobber a peer's concurrent edit on those widgets; on disjoint
 * keys the two writes merge cleanly. This hook is the ONLY place that knows
 * about the split: `liveConfig` stitches gridColumn back onto each widget, and
 * `applyConfig` peels it off — the rest of the app keeps the pre-split shape.
 *
 * Gated: report collab is standard on all tenants but disabled on prod via
 * FEATURES_DISABLED_ON_PROD until stabilized; and requires reports module
 * access (PUBLISH → edit, READ → live read-through). Callers can opt out
 * entirely with `disabled` (public snapshot page, forceReadOnly embeds) —
 * no session is opened at all.
 *
 * Returns:
 *  - collabActive: session is live (synced + fields loaded). When false, the
 *    caller uses the server config + the legacy manual-publish flow untouched.
 *  - collabPending: a session is expected but hasn't synced yet — hold the UI
 *    in loading/read-only until this clears, or edits get discarded.
 *  - canEdit: reports PUBLISH access.
 *  - liveConfig / liveMeta: the live report state (undefined until synced).
 *  - applyConfig(newConfigArray): setConfig replacement — diffs against the live
 *    Y.Doc and routes changes to per-widget setFields (+ __configOrder), so the
 *    existing index-based ConfigForm mutations keep working unchanged.
 *  - setMeta({title, subtitle}), locks, acquire/release, socketId.
 */
export const useReportDocCollabSession = ({ reportId, disabled = false }) => {
  const { user } = useGetQueryData("userData") || {};

  const canEdit = __CheckAccessToModule(user, "PUBLISH", "reports");
  const canRead = canEdit || __CheckAccessToModule(user, "READ", "reports");

  const collab = useCollabDoc({
    enabled: !disabled && !!reportId && canRead,
    docType: "reportDoc",
    contentType: "report", // placeholder segment; the report id is the identity
    contentId: reportId,
  });

  const collabActive = collab.synced && !!collab.fields;

  // The session is coming but isn't up yet. In this window the report must be
  // held in a loading/read-only state: `liveConfig` is still undefined, so any
  // edit would land in the caller's legacy local state and be thrown away the
  // moment `collabActive` flips and the Y.Doc becomes the source.
  // KNOWN GAP: if the sync engine is unreachable this never clears, so the
  // caller's loader spins until it connects. Failure signalling is a follow-up.
  const collabPending = collab.sessionExpected && !collabActive;

  const liveConfig = useMemo(() => {
    if (!collab.fields) return undefined;
    const order = collab.fields.__configOrder || [];
    return order
      .map((id) => {
        const blob = collab.fields[`config.${id}`];
        if (blob == null) return null;
        // Stitch layout back on. Fallback to a gridColumn still inside the
        // blob covers docs seeded before the split; the first structural op
        // writes the layout key and the blob copy stops mattering.
        const gridColumn = collab.fields[`layout.${id}`];
        return gridColumn != null ? { ...blob, gridColumn } : blob;
      })
      .filter((w) => w != null);
  }, [collab.fields]);

  const liveMeta = useMemo(() => {
    if (!collab.fields) return undefined;
    return {
      title: collab.fields.title ?? "",
      subtitle: collab.fields.subtitle ?? "",
    };
  }, [collab.fields]);

  // setConfig replacement. Callers hand us the WHOLE array (they mutate by
  // index) plus the id of the widget they touched. On a content edit we write
  // ONLY that widget's entry (config.<id>) — no array-wide JSON diff, so a
  // sibling widget someone else is editing is never even read, let alone
  // clobbered. STRUCTURAL changes (add / remove / reorder) reconcile the whole
  // set — but their sibling-gridColumn redistribution lands on layout.<id>
  // keys, never on sibling content blobs (see stripLayout).
  const applyConfig = useCallback(
    (nextConfig, changedWidgetId) => {
      const next =
        typeof nextConfig === "function" ? nextConfig(liveConfig || []) : nextConfig;
      const currentOrder = collab.fields?.__configOrder || [];
      const newOrder = next.map((w) => w.id);
      const structural = !arraysEqual(currentOrder, newOrder);

      if (!structural) {
        // Content edit — the caller DECLARES which widget it changed, and we
        // write only that widget's entry. Scoping to the declared id is what
        // keeps peers safe: siblings are never even read, so a collaborator's
        // in-flight edit can't be raced by our (possibly stale) local copy.
        if (changedWidgetId) {
          const w = next.find((x) => x.id === changedWidgetId);
          if (w) {
            collab.setField(`config.${changedWidgetId}`, stripLayout(w));
            // Deliberately NO layout write here. Content writers never move
            // widgets (audited: every gridColumn writer — add / delete / drag —
            // is structural), and carrying layout from the writer's snapshot
            // let a form edit revert a concurrent row-redistribution of this
            // same widget (sibling drags rewrite OUR layout key too, and the
            // widget lock doesn't guard against that). Layout keys are written
            // only by the structural branch below and the no-id fallback.
          }
          return;
        }
        // No id on a non-structural write is a BUG IN THE CALLER — every
        // writer knows which widget it edited and must say so (see
        // effectiveSetConfig). This used to silently drop the write, which is
        // how on-canvas edits (type switch, sentiment toggle) vanished on
        // widgets not under edit. Never lose an edit: recover by diffing each
        // widget against the doc and writing the changed ones — while being
        // loud, because the diff CAN race a peer's concurrent edit (our copy
        // of their widget may be a render behind), which the declared-id path
        // makes impossible.
        console.error(
          "[reportCollab] applyConfig: non-structural config write without changedWidgetId — fix the caller to declare the widget it changed; falling back to a full diff"
        );
        const fields = collab.fields || {};
        next.forEach((w) => {
          const stripped = stripLayout(w);
          if (JSON.stringify(fields[`config.${w.id}`]) !== JSON.stringify(stripped)) {
            collab.setField(`config.${w.id}`, stripped);
          }
          if (
            w.gridColumn !== undefined &&
            JSON.stringify(fields[`layout.${w.id}`]) !== JSON.stringify(w.gridColumn)
          ) {
            collab.setField(`layout.${w.id}`, w.gridColumn);
          }
        });
        return;
      }

      // Structural: membership/order changed. The grid redistribution lands on
      // LAYOUT keys; sibling content blobs compare equal after stripLayout and
      // are skipped — a peer mid-edit on a sibling widget is never overwritten.
      // Content blobs are written only for widgets whose content actually
      // changed (adds, and the transition case of a pre-split blob).
      const fields = collab.fields || {};
      next.forEach((w) => {
        const stripped = stripLayout(w);
        if (JSON.stringify(fields[`config.${w.id}`]) !== JSON.stringify(stripped)) {
          collab.setField(`config.${w.id}`, stripped);
        }
        if (w.gridColumn !== undefined)
          collab.setField(`layout.${w.id}`, w.gridColumn);
      });
      currentOrder.forEach((id) => {
        if (!newOrder.includes(id)) {
          collab.setField(`config.${id}`, null);
          collab.setField(`layout.${id}`, null);
        }
      });
      collab.setField("__configOrder", newOrder);
    },
    [collab, liveConfig]
  );

  const setMeta = useCallback(
    ({ title, subtitle }) => {
      if (title !== undefined) collab.setField("title", title);
      if (subtitle !== undefined) collab.setField("subtitle", subtitle);
    },
    [collab]
  );

  return {
    collabActive,
    collabPending,
    canEdit,
    liveConfig,
    liveMeta,
    applyConfig,
    setMeta,
    locks: collab.locks,
    socketId: collab.socketId,
    acquireLock: collab.acquireLock,
    releaseLock: collab.releaseLock,
    saveStatus: collab.saveStatus,
    flushError: collab.flushError,
  };
};
